import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FilterPopover, type ThemeClasses } from "../../../shared";
import type { StylesConfig } from "react-select";
import useIsMonitor from "../../../../../hooks/useIsMonitor";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";
interface SessionsHeaderProps {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  customSelectStyles: StylesConfig;
  selectedStatus: string;
  setSelectedStatus: (value: string) => void;
  selectedPlatform: string;
  setSelectedPlatform: (value: string) => void;
  search: string;
  setSearch: (value: string) => void;
  onGenerateToken: () => void;
  isGeneratingToken?: boolean;
  disableCRUD?: boolean;
  selectedView: "grid" | "list";
  setSelectedView: (view: "grid" | "list") => void;
  onRefresh: () => void;
  refreshing: boolean;
  refreshCountdown: number;
}

export default function SessionsHeader({
  isDarkMode,
  themeClasses,
  customSelectStyles,
  selectedStatus,
  setSelectedStatus,
  selectedPlatform,
  setSelectedPlatform,
  search,
  setSearch,
  onGenerateToken,
  isGeneratingToken = false,
  disableCRUD = false,
  selectedView,
  setSelectedView,
  onRefresh,
  refreshing,
  refreshCountdown,
}: SessionsHeaderProps) {
  const { t } = useTranslation("adminSections");
  const { isMonitor, isReadOnly } = useIsMonitor();
  const statusOptions = [
    { value: "all", label: t("sessions.status.all") },
    { value: "true", label: t("sessions.status.active") },
    { value: "false", label: t("sessions.status.inactive") },
  ];

  const platformOptions = [
    { value: "all", label: t("sessions.platform.all") },
    { value: "Web", label: t("sessions.platform.web") },
    { value: "ios", label: t("sessions.platform.ios") },
    { value: "android", label: t("sessions.platform.android") },
  ];

  return (
    <div className={`${themeClasses.bg} px-4 py-3 sm:px-6 sm:py-4`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
        <div className="min-w-0">
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            {t("sessions.title")}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
            {t("sessions.subtitle")}
          </p>
        </div>
        {!isMonitor && !isReadOnly && (
          <CRUDTooltip className="self-start sm:self-auto flex-shrink-0">
            <button
              onClick={onGenerateToken}
              disabled={isGeneratingToken || disableCRUD}
              className={`bg-primary-500 hover:bg-[#5ed83a] text-black px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed ${
                isGeneratingToken ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isGeneratingToken ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-key-2-line"></i>
              )}
              <span className="hidden sm:inline">
                {isGeneratingToken
                  ? t("sessions.apiToken.generating")
                  : t("sessions.generateToken")}
              </span>
              <span className="sm:hidden">Token</span>
            </button>
          </CRUDTooltip>
        )}
      </div>

      <div className="flex items-center gap-2 min-w-0">
        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
          <i
            className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
          ></i>
          <input
            type="text"
            placeholder={t("sessions.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`pl-9 pr-3 py-1.5 h-9 ${
              isDarkMode ? "bg-dark-card" : "bg-gray-50"
            } border ${themeClasses.cardBorder} rounded-lg ${
              themeClasses.text
            } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-full`}
          />
        </div>

        <FilterPopover
          isDarkMode={isDarkMode}
          activeCount={
            (selectedStatus !== "all" ? 1 : 0) +
            (selectedPlatform !== "all" ? 1 : 0)
          }
          onClear={() => {
            setSelectedStatus("all");
            setSelectedPlatform("all");
          }}
        >
          <Select
            options={statusOptions}
            value={statusOptions.find(
              (status) => status.value === selectedStatus,
            )}
            onChange={(option: any) => setSelectedStatus(option.value)}
            styles={customSelectStyles}
            isSearchable={false}
            placeholder={t("sessions.filterByStatus")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />

          <Select
            options={platformOptions}
            value={platformOptions.find(
              (platform) => platform.value === selectedPlatform,
            )}
            onChange={(option: any) => setSelectedPlatform(option.value)}
            styles={customSelectStyles}
            isSearchable={false}
            placeholder={t("sessions.filterByPlatform")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </FilterPopover>

        {/* View Toggle and Refresh */}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {/* Refresh Button */}
          <div className="flex items-center gap-1">
            <button
              onClick={onRefresh}
              disabled={refreshing || refreshCountdown > 0}
              className={`p-2 rounded-lg transition-colors flex items-center justify-center ${
                refreshing || refreshCountdown > 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : `${themeClasses.buttonHover} ${themeClasses.buttonText}`
              }`}
              title={
                refreshCountdown > 0
                  ? `Espere ${refreshCountdown} segundos para refrescar`
                  : "Refrescar sesiones"
              }
            >
              {refreshing ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-refresh-line"></i>
              )}
            </button>

            {/* Countdown Counter */}
            {refreshCountdown > 0 && (
              <span
                className={`text-xs font-medium ${themeClasses.textSecondary} min-w-[20px] text-center`}
              >
                {refreshCountdown}
              </span>
            )}
          </div>

          {/* View Toggle */}
          <div
            className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
          >
            <button
              className={`px-3 py-1 rounded-md transition-colors ${
                selectedView === "grid"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
              }`}
              onClick={() => setSelectedView("grid")}
            >
              <i className="ri-grid-fill"></i>
            </button>
            <button
              className={`px-3 py-1 rounded-md transition-colors ${
                selectedView === "list"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
              }`}
              onClick={() => setSelectedView("list")}
            >
              <i className="ri-list-check"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
